<!-- eslint-disable vuejs-accessibility/no-static-element-interactions -->
<template>
  <div class="bna-compare">
    <figure class="bna-compare__after">
      <slot name="after">
        <picture>
          <source media="(min-width: 767px)" :srcset="afterImage.md" />
          <img :src="afterImageSource" :alt="afterImageAlt" width="100%" :loading="loading" />
        </picture>
      </slot>
      <slot name="after-caption">
        <figcaption v-if="!!afterLabel" v-text="afterLabel"></figcaption>
      </slot>
    </figure>

    <div class="bna-compare__overlay" :style="{ '--position': position + '%' }">
      <figure class="bna-compare__before">
        <slot name="before">
          <picture>
            <source media="(min-width: 767px)" :srcset="beforeImage.md" />
            <img :src="beforeImageSource" :alt="beforeImageAlt" width="100%" :loading="loading" />
          </picture>
        </slot>
        <slot name="before-caption">
          <figcaption v-if="!!beforeLabel" v-text="beforeLabel"></figcaption>
        </slot>
      </figure>

      <div class="bna-compare__handle-container">
        <div
          role="separator"
          class="bna-compare__handle"
          tabindex="0"
          :aria-label="buttonAriaLabel"
          aria-valuemin="0"
          aria-valuemax="100"
          :aria-valuenow="position"
          @keydown="hndKey"
          @mousedown="hndMouseDown"
          @touchstart="hndTouchStart"
        >
          <slot name="handle"></slot>
        </div>
      </div>
    </div>

    <slot name="extra"></slot>
  </div>
</template>

<script>
import { dynamicCompareProps as props, defaultKeyboardStep } from './prop-definitions';

export default {
  name: 'refapp-beforeafter-compare',
  inject: [
    'beforeLabel',
    'afterLabel',
    'buttonAriaLabel',
    'loading',
    'keyboardStep',
    'initialPosition',
    'direction',
  ],
  props,
  data() {
    return {
      // percentage the after image being exposed
      position: Math.abs(100 - this.initialPosition),
      hasTouchCapabilities: window.matchMedia('(pointer: coarse)').matches,
      bnaslideDebounceTimeoutId: null,
    };
  },
  computed: {
    beforeImageSource() {
      return this.beforeImage?.sm || this.beforeImage.md;
    },

    afterImageSource() {
      return this.afterImage?.sm || this.afterImage.md;
    },
  },
  watch: {
    beforeImage() {
      this.position = Math.abs(100 - this.initialPosition);
    },
  },
  unmounted() {
    this.handleUnmount();
  },
  destroyed() {
    this.handleUnmount();
  },
  methods: {
    handleUnmount() {
      if (this.bnaslideDebounceTimeoutId) {
        clearTimeout(this.bnaslideDebounceTimeoutId);
        this.bnaslideDebounceTimeoutId = null;
      }
    },
    // take a mouse or touch event and output an object with x and y coordinates
    getPagePoint(ev) {
      if (this.hasTouchCapabilities) {
        return {
          x: ev.touches[0].pageX,
          y: ev.touches[0].pageY,
        };
      }

      return {
        x: ev.pageX,
        y: ev.pageY,
      };
    },
    // interpret and manipulate the input event and call the slider position update chain
    getNewPositionFromEvent(ev) {
      const currentPoint = this.getPagePoint(ev);
      ev.stopPropagation();

      this.setSliderPosition(currentPoint);
    },
    // set the slider position to a specific point, depending on layout direction
    setSliderPosition(point) {
      // future: upgrade for vertical scrubbing
      if (this.direction === 'horizontal') {
        this.setSliderPositionX(point.x);
      }
    },
    // set the slider position for a horizontal layout and fire an update event
    setSliderPositionX(pageX) {
      const imageBox = this.$el.getBoundingClientRect();
      const x = pageX - imageBox.left - window.scrollX;
      const newPosition = 100 - (x / imageBox.width) * 100;

      this.updatePositionValue(newPosition);
    },
    // clean up window-set mouse listeners
    clearMouseMoveListener() {
      window.removeEventListener('mousemove', this.getNewPositionFromEvent);
      window.removeEventListener('mouseup', this.clearMouseMoveListener);
    },
    // clean up window-set touch listeners
    clearTouchMoveListener() {
      window.removeEventListener('touchmove', this.getNewPositionFromEvent);
      window.removeEventListener('touchend', this.clearTouchMoveListener);
    },
    // when presing the mouse on the handle, add listener to track its movement
    // and another to remove these listeners once the mouse has been released
    hndMouseDown() {
      window.addEventListener('mousemove', this.getNewPositionFromEvent);
      window.addEventListener('mouseup', this.clearMouseMoveListener);
    },
    // when starting to swipe on the handle, add listener to track the movement
    // and another to remove these listeners once the gesture has ended
    hndTouchStart() {
      window.addEventListener('touchmove', this.getNewPositionFromEvent);
      window.addEventListener('touchend', this.clearTouchMoveListener);
    },
    // common function to update the position value and emit the update event
    updatePositionValue(newValue) {
      // limit value between 0 and 100 so we don't lose the handle
      this.position = Math.max(0, Math.min(newValue, 100));

      if (this.bnaslideDebounceTimeoutId) {
        clearTimeout(this.bnaslideDebounceTimeoutId);
        this.bnaslideDebounceTimeoutId = null;
      }

      this.bnaslideDebounceTimeoutId = setTimeout(() => {
        const htmlEvent = new CustomEvent('bnaslide', {
          detail: { position: this.position },
        });
        this.$el.dispatchEvent(htmlEvent);
        this.bnaslideDebounceTimeoutId = null;
      }, 1000);
    },
    // keyboard arrow handlers
    hndKey(ev) {
      const keysToPrevent = ['ArrowRight', 'ArrowLeft', 'Home', 'End'];
      if (keysToPrevent.includes(ev.key)) {
        ev.preventDefault();
      }
      // this input may come as a string, parse it to Int
      let step = parseInt(this.keyboardStep, 10);
      step = isNaN(step) ? defaultKeyboardStep : step;

      switch (ev.key) {
        case 'ArrowRight':
          this.updatePositionValue(this.position - step);
          break;
        case 'ArrowLeft':
          this.updatePositionValue(this.position + step);
          break;
        case 'Home':
          this.updatePositionValue(100);
          break;
        case 'End':
          this.updatePositionValue(0);
          break;
        // TODO: add vertical layout
      }
    },
  },
};
</script>
